/**
 * Normalize a financing phone-field value, treating anything too short to be a
 * real number as empty.
 *
 * The phone inputs render a "+1" country-code prefix that the widget injects on
 * focus, so a field the user focused but never filled holds just the prefix
 * ("+1"). Any value of 9 characters or fewer (the bare prefix, or a half-typed
 * number) is treated as empty. This is the same rule the application-submit
 * path applies, so the verification gate and the submitted application agree on
 * which numbers count as "present".
 */
export const toPhone = (phone: string): string =>
    !phone || phone.length <= 9 ? "" : phone;

/**
 * Pick the first phone field that actually holds a number, for the ownership-
 * verification gate. Candidates are tried in priority order (e.g. mobile before
 * home). A country-code-only value like "+1" is skipped — using plain `||`
 * truthiness here would wrongly prefer it over a real number in a later field,
 * which is what sent `{"phone":"+1"}` to the verification endpoint. Returns
 * null when no candidate holds a usable number.
 */
export const pickVerificationPhone = (
    ...candidates: readonly string[]
): string | null => {
    for (const candidate of candidates) {
        const phone = toPhone(candidate);
        if (phone) {
            return phone;
        }
    }
    return null;
};
